import React, { useState, useMemo } from 'react';
import { FiSearch, FiChevronLeft } from 'react-icons/fi';
import { IoHelpCircle } from 'react-icons/io5';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import useFetchAllCategories from '../../Hooks/useFetchAllCategories';
import type { Category, Subcategory } from '../../Hooks/useFetchAllCategories';
import HowItWorksModal from '../../features/home/components/HowItWorksModal';
import BottomSheet from './BottomSheet';

// Phones: one swipeable row of round category icons (the most popular first),
// so the ads start on the first screen. Tapping a category opens its subcategories.
const CategoriesCard: React.FC = () => {
    const navigate = useNavigate();
    const [sheetOpen, setSheetOpen] = useState(false);
    const [howItWorksOpen, setHowItWorksOpen] = useState(false);
    const [selectedCategory, setSelectedCategory] = useState<Category | null>(null);
    const [search, setSearch] = useState('');
    const { categories, subcategories, loading, error } = useFetchAllCategories();

    const sortedCategories = useMemo(
        () => [...categories].sort((a, b) => (b.adCount || 0) - (a.adCount || 0)),
        [categories]
    );

    // Filter subcategories by selected category and search, the most used first
    const filteredSubcategories = useMemo(() => {
        if (!selectedCategory) return [];
        return subcategories
            .filter(sc => sc.category_id === selectedCategory.id)
            .filter(sc => sc.name.toLowerCase().includes(search.toLowerCase()))
            .sort((a, b) => (b.adCount || 0) - (a.adCount || 0));
    }, [selectedCategory, subcategories, search]);

    // Handle category tap
    const handleCategoryTap = (cat: Category) => {
        setSelectedCategory(cat);
        setSheetOpen(true);
        setSearch('');
    };

    // Handle subcategory tap (in-app navigation: no full page reload)
    const handleSubcategoryTap = (subcat: Subcategory) => {
        if (selectedCategory) {
            setSheetOpen(false);
            navigate(`/search/${encodeURIComponent(selectedCategory.name)}/${encodeURIComponent(subcat.name)}`);
        }
    };

    const stripClass = "flex gap-3 overflow-x-auto px-3 py-3 snap-x [scrollbar-width:none] [&::-webkit-scrollbar]:hidden";

    if (loading) return (
        <div className={stripClass}>
            {[...Array(6)].map((_, index) => (
                <div key={index} className="shrink-0 w-[72px] flex flex-col items-center">
                    <div className="w-14 h-14 rounded-full bg-gray-200 animate-pulse" />
                    <div className="h-3 w-12 bg-gray-200 rounded-full mt-2 animate-pulse" />
                </div>
            ))}
        </div>
    );
    if (error) return null;

    const item = "snap-start shrink-0 w-[72px] flex flex-col items-center";
    const label = "mt-1.5 text-[11px] leading-tight font-medium text-content-default text-center line-clamp-2";

    return (
        <div className="w-full">
            <div className={stripClass}>
                <motion.button whileTap={{ scale: 0.92 }} className={item} onClick={() => setHowItWorksOpen(true)}>
                    <div className="w-14 h-14 rounded-full bg-primary-default flex items-center justify-center shadow-sm">
                        <IoHelpCircle size={30} className="text-white" />
                    </div>
                    <span className={label}>Comment ça marche ?</span>
                </motion.button>

                {sortedCategories.map((cat) => (
                    <motion.button
                        key={cat.id}
                        whileTap={{ scale: 0.92 }}
                        className={item}
                        onClick={() => handleCategoryTap(cat)}
                    >
                        <div className="w-14 h-14 rounded-full bg-white border border-primary-tertiary/50 shadow-sm flex items-center justify-center overflow-hidden">
                            <img
                                src={cat.image || '/default-category-image.png'}
                                alt=""
                                loading="lazy"
                                className="w-9 h-9 object-contain"
                            />
                        </div>
                        <span className={label}>{cat.name}</span>
                    </motion.button>
                ))}
            </div>

            <HowItWorksModal isOpen={howItWorksOpen} onClose={() => setHowItWorksOpen(false)} />

            {/* BottomSheet for subcategories */}
            <BottomSheet
                open={sheetOpen}
                onClose={() => setSheetOpen(false)}
                title={
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => setSheetOpen(false)}
                            className="text-xl"
                            aria-label="Retour"
                        >
                            <FiChevronLeft />
                        </button>
                        <span className="truncate max-w-[200px]">{selectedCategory?.name}</span>
                    </div>
                }
            >
                <div className="mb-2">
                    <div className="flex items-center bg-gray-100 rounded-lg px-2 py-1">
                        <FiSearch className="text-gray-400 mr-2" />
                        <input
                            type="text"
                            placeholder="Rechercher une sous-catégorie"
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            className="bg-transparent outline-none flex-1 py-1 text-sm"
                        />
                    </div>
                </div>
                <div className="flex flex-col gap-2 max-h-72 overflow-y-auto">
                    {filteredSubcategories.length === 0 && (
                        <div className="text-gray-400 text-center py-8">Aucune sous-catégorie</div>
                    )}
                    {filteredSubcategories.map((subcat, index) => (
                        <motion.button
                            key={subcat.id}
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: index * 0.05 }}
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            className="flex items-center gap-3 p-3 rounded-lg bg-gray-50 hover:bg-primary-tertiary/30 transition"
                            onClick={() => handleSubcategoryTap(subcat)}
                        >
                            <img
                                src={subcat.image || '/default-subcategory-image.png'}
                                alt={subcat.name}
                                className="w-10 h-10 rounded object-cover border border-gray-200"
                            />
                            <span className="text-sm font-medium text-gray-800 flex-1 text-left truncate">
                                {subcat.name}
                            </span>
                            <span className="bg-primary-tertiary/40 text-primary-default text-xs px-2 py-0.5 rounded-full">
                                {subcat.adCount ?? 0} articles
                            </span>
                        </motion.button>
                    ))}
                </div>
            </BottomSheet>
        </div>
    );
};

export default CategoriesCard;
